import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { RingsField } from "@/components/Rings";
import { BRANDS } from "@/data/brands";

const HeroRings = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 18]);
  return (
    <motion.div
      ref={ref}
      style={{ y, rotate }}
      className="pointer-events-none absolute -right-32 top-1/2 -translate-y-1/2 w-[420px] sm:w-[560px] opacity-70"
      aria-hidden="true"
    >
      <RingsField />
    </motion.div>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Antelli — A family of five brands"
        description="Antelli is the company behind five consumer brands in travel, relocation, short-stay property and publishing. Run from Bribie Island, Queensland."
      />

      <section className="relative overflow-hidden">
        <HeroRings />
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-24 pb-20 sm:pt-36 sm:pb-28 relative">
          <h1
            data-testid="home-hero-heading"
            className="font-serif-display text-[#1A1C23] text-5xl sm:text-7xl lg:text-8xl leading-[1.04] font-medium"
          >
            <MaskedLine delay={0.15}>Dream it.</MaskedLine>
            <MaskedLine delay={0.3}>Plan it.</MaskedLine>
            <MaskedLine delay={0.45}>
              Live it<span className="text-[#987E54]">.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-8">
            <p
              data-testid="home-hero-tagline"
              className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#7A6034]"
            >
              From Queensland to the world
            </p>
          </MaskedLine>
          <Reveal delay={0.9} className="mt-14 sm:mt-20 max-w-xl">
            <p data-testid="home-intro" className="text-base sm:text-lg leading-relaxed text-[#4A4D57]">
              Antelli is the company behind five consumer brands — in travel,
              relocation, short-stay property and publishing. It has no
              customers of its own and sells nothing here. It exists to give
              the five brands a home, a standard and a name in common.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-32">
        <Reveal>
          <div className="flex items-baseline justify-between gap-6 border-b border-[#1A1C23]/10 pb-6">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A1C23]">
              The brands
            </h2>
            <Link
              to="/brands"
              data-testid="home-brands-more-link"
              className="text-sm text-[#7A6034] hover:text-[#1A1C23] transition-colors duration-300 whitespace-nowrap"
            >
              Read more
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1C23]/10 border border-[#1A1C23]/10 mt-12">
          {BRANDS.map((brand, i) => (
            <Reveal key={brand.id} delay={i * 0.08} className="bg-[#F4F3EF]">
              <a
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`home-brand-card-${brand.id}`}
                className="group block h-full p-8 sm:p-10 transition-colors duration-500 hover:bg-white"
              >
                <div className="flex items-center justify-between">
                  <span
                    className="font-serif-display text-sm"
                    style={{ color: brand.accent }}
                  >
                    {brand.number}
                  </span>
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#757882] transition-all duration-300 group-hover:text-[#7A6034] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
                <h3 className="mt-8 font-serif-display text-2xl text-[#1A1C23]">
                  {brand.name}
                </h3>
                <span
                  className="mt-3 block h-px w-10 transition-all duration-500 group-hover:w-16"
                  style={{ backgroundColor: brand.accent }}
                />
                <p className="mt-5 text-sm leading-relaxed text-[#4A4D57]">
                  {brand.short}
                </p>
              </a>
            </Reveal>
          ))}
          <Reveal delay={0.4} className="bg-[#F4F3EF]">
            <Link
              to="/contact"
              data-testid="home-contact-card"
              className="group flex h-full flex-col justify-between p-8 sm:p-10 transition-colors duration-500 hover:bg-[#1A1C23]"
            >
              <span className="text-xs uppercase tracking-[0.25em] text-[#757882] group-hover:text-[#B69766] transition-colors duration-500">
                Enquiries
              </span>
              <span className="mt-10 font-serif-display text-2xl leading-snug text-[#1A1C23] group-hover:text-[#F4F3EF] transition-colors duration-500">
                Partnerships, suppliers, press —{" "}
                <span className="italic text-[#987E54] group-hover:text-[#B69766]">
                  get in touch
                </span>
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
